import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, filter, Observable, of, shareReplay, switchMap, tap, startWith, map, delay } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Platform } from '@angular/cdk/platform';
import { UserPermissionsService } from '@portal/shared/services/user-permissions.service';
import { UsersPartyPermissionsReadType } from '@portal/api/enums/users-party-permissions-read-type';
import { Permissions, PermissionsByUserResponse } from '@portal/api/responses/permissions/permissions-by-user-response';
import { UsersOrganizationsPermissionsReadType } from '@portal/api/enums/users-organizations-permissions-read-type';
import { OrganizationSelectorService } from '@portal/shared/services/organization-selector.service';
import { PartySelectedService } from '@portal/shared/services/party-selected.service';
import { StorageService } from '@portal/shared/services/storage.service';
import { EventNavigationService } from './event-navigation.service';
import { Router, NavigationEnd } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { OrganizationType } from '@portal/api/enums/organization-type';
import { OrganizationEntitlement } from '@portal/api/enums/organization-entitlement';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private _navigation: BehaviorSubject<Navigation | null> = new BehaviorSubject<Navigation | null>(null);
    private _currentUrl: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private _isTouchDevice: boolean;

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
                private _partySelectedService: PartySelectedService,
                private _organizationSelectorService: OrganizationSelectorService,
                private _permissionsService: UserPermissionsService,
                private _storageService: StorageService,
                private _eventNavigationService: EventNavigationService,
                private _router: Router,
                private _platform: Platform
    ) {
        this._isTouchDevice = this._platform.ANDROID || this._platform.IOS;
        // Track URL changes
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this._currentUrl.next(event.url);
            this.get().subscribe();
        });

        // Initialize with current URL
        this._currentUrl.next(this._router.url);

        this._permissionsService.currentPermissions$.subscribe(res => {
            this.get().subscribe();
        });

        this._organizationSelectorService.org$.subscribe(() => {
            this.get().subscribe();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable().pipe(filter((item): item is Navigation => item !== null), shareReplay());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return combineLatest([
            this.getNavigationList().pipe(switchMap(this.filterByNavigation())),
            this._eventNavigationService.eventNavigationItems$
        ]).pipe(
            delay(0), // Small delay to ensure router has resolved
            tap(([navigation, eventItems]) => {

                // Get the current URL directly from the router
                const currentUrl = this._router.url;
                const isOnEventRoute = currentUrl.includes('/party/events/') && currentUrl.split('/').length > 4;
                const isRootPath = currentUrl === '/';

                const eventosGroup = navigation.default.find(item => item.id === 'parties');
                if (eventosGroup) {
                    if (this._isTouchDevice) {
                        eventosGroup.collapsed = false;
                    }

                    // Keep the "Eventos" group expanded when on party-related routes
                    // Keep the "Eventos" group expanded when on party-related routes
                    const isOnPartyRoute = currentUrl.includes('/party/');
                    if (isOnPartyRoute) {
                        eventosGroup.collapsed = false;
                    }
                    
                    if (isOnEventRoute) {
                        // Prevent the "Actividades" item from being active
                        const actividadesItem = eventosGroup.children?.find(child => child.id === 'events');
                        if (actividadesItem) {
                            actividadesItem.active = false;
                        }
                    }
                }

                // Skip navigation logic if on root path
                if (isRootPath) {
                    return;
                }

                // Remove any event navigation items from main navigation (they will be shown in a separate menu)
                navigation.default = navigation.default.filter(item =>
                    !(item.id && item.id.startsWith('current-event-'))
                );

                navigation.default = navigation.default.filter(x => !x.children || x.children.length !== 0);
                this._navigation.next(navigation);
            }),
            switchMap(([navigation]) => of(navigation))
        );
    }

    private filterByNavigation() {
        return (navigation: Navigation) => {
            this.setAdminMenuItems(navigation);

            const orgType = this._organizationSelectorService.org?.type;
            if (orgType === OrganizationType.Association) {
                const userRoles = this._storageService.getTokenRoles();
                const isPlatformAdmin = userRoles.includes('Admin') || userRoles.includes('Manager');
                const partiesGroup = navigation.default.find(item => item.id === 'parties');
                const revenuesItem = partiesGroup?.children?.find(child => child.id === 'revenues');
                const expensesItem = partiesGroup?.children?.find(child => child.id === 'expenses');
                const barsItem = partiesGroup?.children?.find(child => child.id === 'bars');
                const eventsItem = partiesGroup?.children?.find(child => child.id === 'events');
                const inventoryItem = partiesGroup?.children?.find(child => child.id === 'inventory');
                const merchandisingItem = partiesGroup?.children?.find(child => child.id === 'merchandising');
                const donationsItem = partiesGroup?.children?.find(child => child.id === 'donations');
                const sponsorsItem = partiesGroup?.children?.find(child => child.id === 'sponsors');
                const collaboratorsItem = partiesGroup?.children?.find(child => child.id === 'collaborators');
                const usersItem = partiesGroup?.children?.find(child => child.id === 'users');
                const canReadOrgRevenues = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.RevenuesRead
                );
                const canReadOrgExpenses = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.ExpensesRead
                );
                const canReadOrgDonations = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.DonationRead
                );
                const canReadOrgEvents = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.EventsRead
                );
                const canReadOrgSponsors = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.SponsorsRead
                );
                const canReadOrgOwner = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.IsOwner
                );
                const hasEventsEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Events);
                const hasRevenuesEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Revenues);
                const hasExpensesEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Expenses);
                const hasDonationsEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Donations);
                const hasSponsorsEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Sponsors);
                const hasBarsEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Bars);
                const hasCollaboratorsEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Collaborators);
                const hasInventoryEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Inventory);
                const hasMerchandisingEntitlement = this._permissionsService.hasOrganizationEntitlement(OrganizationEntitlement.Merchandising);
                const canReadOrgBars = this._permissionsService.hasOrganizationReadPermissionValue(
                    UsersOrganizationsPermissionsReadType.BarsRead
                ) || canReadOrgRevenues || canReadOrgExpenses;
                const selectedOrgId = String(this._organizationSelectorService.org?.id || this._storageService.getOrg()?.id || '').trim();
                const associationDashboard: FuseNavigationItem | null = selectedOrgId
                    ? {
                        id: 'organization-dashboard',
                        title: 'Painel',
                        type: 'basic',
                        icon: 'mat_outline:dashboard',
                        link: '/organizations/dashboard',
                    }
                    : null;
                const associationRevenues = (revenuesItem && canReadOrgRevenues && hasRevenuesEntitlement)
                    ? { ...revenuesItem, needParty: false }
                    : null;
                const associationClubMembers: FuseNavigationItem | null = (canReadOrgRevenues && hasRevenuesEntitlement)
                    ? {
                        id: 'club-members',
                        title: 'Sócios',
                        type: 'basic',
                        icon: 'heroicons_outline:user-group',
                        link: '/organizations/club-members',
                    }
                    : null;
                const associationExpenses = (expensesItem && canReadOrgExpenses && hasExpensesEntitlement)
                    ? { ...expensesItem, needParty: false }
                    : null;
                const associationBars = (barsItem && canReadOrgBars && hasBarsEntitlement)
                    ? { ...barsItem, needParty: false }
                    : null;
                const associationEvents = (eventsItem && canReadOrgEvents && hasEventsEntitlement)
                    ? { ...eventsItem, needParty: false, link: '/party/events' }
                    : null;
                const associationInventory = (inventoryItem && canReadOrgEvents && hasInventoryEntitlement)
                    ? { ...inventoryItem, needParty: false, link: '/party/inventory' }
                    : null;
                const associationMerchandising = (merchandisingItem && canReadOrgEvents && hasMerchandisingEntitlement)
                    ? {
                        ...merchandisingItem,
                        needParty: false,
                        link: selectedOrgId ? `/org/${selectedOrgId}/merchandising/overview` : '/party/merchandising/overview'
                    }
                    : null;
                const associationDonations = (donationsItem && canReadOrgDonations && hasDonationsEntitlement)
                    ? { ...donationsItem, needParty: false, link: '/party/donations/list' }
                    : null;
                const associationSponsors = (sponsorsItem && canReadOrgSponsors && hasSponsorsEntitlement)
                    ? { ...sponsorsItem, needParty: false }
                    : null;
                const associationCollaborators = (collaboratorsItem && canReadOrgRevenues && hasCollaboratorsEntitlement)
                    ? { ...collaboratorsItem, needParty: false }
                    : null;
                const associationUsers = (usersItem && canReadOrgOwner)
                    ? { ...usersItem, needParty: false, link: '/organizations/users' }
                    : null;
                navigation.default = [
                    ...(associationDashboard ? [associationDashboard] : []),
                    ...(associationClubMembers ? [associationClubMembers] : []),
                    ...(associationRevenues ? [associationRevenues] : []),
                    ...(associationExpenses ? [associationExpenses] : []),
                    ...(associationBars ? [associationBars] : []),
                    ...(associationEvents ? [associationEvents] : []),
                    ...(associationInventory ? [associationInventory] : []),
                    ...(associationMerchandising ? [associationMerchandising] : []),
                    ...(associationDonations ? [associationDonations] : []),
                    ...(associationSponsors ? [associationSponsors] : []),
                    ...(associationCollaborators ? [associationCollaborators] : []),
                    ...(associationUsers ? [associationUsers] : []),
                    ...(isPlatformAdmin ? navigation.default.filter(item => item.id === 'admin') : [])
                ];
                return of(navigation);
            }

            const partyId = this._partySelectedService.getPartyId();
            if (!partyId) {
                this.setNoPartyMenuItems(navigation);
            }
            if (!this._organizationSelectorService.hasOrgSelected()) {
                this.setNoOrgMenuItems(navigation);
            }

            const orgTypeCurrent = this._organizationSelectorService.org?.type;
            if (orgTypeCurrent !== OrganizationType.Association) {
                navigation.default = navigation.default.map(item => {
                    item.children = item.children?.filter(child => !(child as any).needAssociation);
                    return item;
                });
            }

            if (!this._organizationSelectorService.hasOrgSelected() && !partyId) {
                return of(navigation);
            }

            if (!this._permissionsService.currentPermissions) {
                return of(navigation);
            }
            const permissionsList = this.setReadPermissions(this._permissionsService.currentPermissions);
            return of(this.filterMenuItemsByReadPermissions(navigation, permissionsList));
        };
    }

    private setNoPartyMenuItems(navigation: Navigation) {
        navigation.default = navigation.default.map(x => {
            x.children = x.children?.filter(c => !c.needParty);
            return x;
        });
    }
    private setAdminMenuItems(navigation: Navigation) {
        const userRoles = this._storageService.getTokenRoles();
        // Filtrar os itens de nível superior com base nas roles do usuário
        navigation.default = navigation.default.filter((item) => {
            // Se o item de nível superior não tiver roles, mantém.
            if (!item.roles || item.roles.length === 0) {
                return true;
            }

            // Verifica se o usuário tem pelo menos uma das roles necessárias para o item de nível superior.
            const hasRequiredRole = item.roles.some(role => userRoles.includes(role));

            // Se o item de nível superior não tiver a role necessária, não precisa verificar os children.
            if (!hasRequiredRole) {
                return false;
            }

            // Se o item de nível superior tiver children, filtra os children também.
            if (item.children) {
                item.children = item.children.filter((child) => {
                    // Se o child não tiver roles, mantém.
                    if (!child.roles || child.roles.length === 0) {
                        return true;
                    }

                    // Verifica se o usuário tem pelo menos uma das roles necessárias para o child.
                    return child.roles.some(role => userRoles.includes(role));
                });
            }

            // Mantém o item de nível superior se passou pelas verificações acima.
            return true;
        });

        // ...
    }


    private setNoOrgMenuItems(navigation: Navigation) {
        navigation.default = navigation.default.map(x => {
            x.children = x.children?.filter(c => !c.needOrg);
            return x;
        });
    }

    private filterMenuItemsByReadPermissions(navigation: Navigation, permissionsList: Permissions) {
        navigation.default = navigation.default.map(x => {
            const isOrgSubMenu = x.type === 'group' && x.id === 'orgs';
            const isPartiesSubMenu = x.type === 'group' && x.id === 'parties';
            const isAdminMenu = x.type === 'group' && x.id === 'admin';

            if (isAdminMenu) {
                const roles = this._storageService.getTokenRoles();
            }
            if (!x.children || x.children.length === 0) {
                return x;
            }

            x.children = x.children.filter(c => {
                if (!c.permissions) {
                    return true;
                }
                if (isAdminMenu) {
                    return true;
                }

                if (isOrgSubMenu) {
                    return c.permissions.every(p =>
                        (permissionsList.orgs.includes(p) || permissionsList.orgs.includes(UsersPartyPermissionsReadType.IsOwner)));
                }

                if (isPartiesSubMenu) {
                    return c.permissions.every(p =>
                        (permissionsList.party.includes(p) || permissionsList.party.includes(UsersPartyPermissionsReadType.IsOwner)));
                }


            });

            return x;
        });
        return navigation;
    }

    private setReadPermissions(permissions: PermissionsByUserResponse): Permissions {
        if (!permissions) {
            return { party: [], orgs: [] };
        }

        const partyPermissions = [];
        const permissionValues = Object.values(UsersPartyPermissionsReadType)
            .filter(value => !isNaN(Number(value))).map(Number);
        for (let i = 0; i < permissionValues.length; i++) {
            const permissionValue = permissionValues[i];
            if (permissions.partyRead & permissionValue) {
                partyPermissions.push(permissionValue);
            }
        }


        const orgsPermissions = [];
        const orgsPermissionValues = Object.values(UsersOrganizationsPermissionsReadType)
            .filter(value => !isNaN(Number(value))).map(Number);
        for (let i = 0; i < orgsPermissionValues.length; i++) {
            const permissionValue = orgsPermissionValues[i];
            if (permissions.orgRead & permissionValue) {
                orgsPermissions.push(permissionValue);
            }
        }


        return { party: partyPermissions, orgs: orgsPermissions };
    }


    private getNavigationList(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation');
    }
}
